import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {USER_TYPE_DOCUMENT} from "../../../../../enums/userTypeEnum";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";
import {TABLES} from "../../../../../Apis/Tables";
import routes from "../../../../../Routes/routes";
import {GetProviderEmployeeDetail} from "../../../../../reduxToolkit/Providers/providersApi";

/**
 * @file EmployeeDocumentDetailApp.jsx
 * @description Custom hook that manages the state and logic for handling provider employee documents,
 * including deletion, selection, and data refresh operations.
 *
 * @email azapata@ibl.mx
 * @author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 */

/**
 * Custom hook that handles logic and state for managing provider employee document details.
 *
 * This hook supports:
 * - Fetching employee details based on route ID
 * - Managing selection state for documents and employees
 * - Deleting selected documents or employees
 * - Reacting to image or document changes via Redux state
 *
 * @returns {Object} An object containing:
 * @returns {Function} deleteSelectedDocuments - Function to delete selected documents.
 * @returns {Function} deleteSelectedElement - Function to delete a selected employee.
 * @returns {Array} selectDocumentsForDelete - Array of selected document IDs.
 * @returns {Function} setSelectDocumentsForDelete - Setter for selected documents array.
 * @returns {boolean} isAllChecked - Flag indicating if all documents are selected.
 * @returns {Function} setIsAllChecked - Setter for `isAllChecked`.
 * @returns {boolean} deleteDocShow - Flag to toggle delete confirmation modal visibility.
 * @returns {Function} setDeleteDocShow - Setter for `deleteDocShow`.
 * @returns {number} deletedFlag - Trigger used to refetch employee details.
 * @returns {Function} setDeletedFlag - Setter for `deletedFlag`.
 * @returns {Array|undefined} selectEmployeeForDelete - Selected employee ID(s) to delete.
 * @returns {Function} setSelectEmployeeForDelete - Setter for `selectEmployeeForDelete`.
 * @returns {Object} elementDetails - Fetched employee detail object from Redux.
 * @returns {Function} t - i18n translation function.
 */

export const EmployeeDocumentDetailApp =()=>{
    const dispatch = useDispatch(); // Hook to dispatch Redux actions
    const navigate = useNavigate(); // Hook to navigate between routes
    const { t } = useTranslation(); // Hook to handle translations
    const { id } = useParams(); // Extract the employee ID from the route parameters

    // Local state variables
    const [selectDocumentsForDelete, setSelectDocumentsForDelete] = useState([]); // Tracks selected documents for deletion
    const [isAllChecked, setIsAllChecked] = useState(false); // Flag to track "select all" checkbox status
    const [deleteDocShow, setDeleteDocShow] = useState(false); // Toggle for showing the document delete confirmation
    const [deletedFlag, setDeletedFlag] = useState(0); // Trigger for re-fetching data after deletion
    const [selectEmployeeForDelete, setSelectEmployeeForDelete] = useState(); // Tracks selected employee for deletion

    // Redux state selectors
    const {
        getProviderEmployeeDetail,
        setSupplierDocValue,
        createSupplierDocValue,
        saveProviderImage,
        imageChangeCheck
    } = useSelector((state) => state?.providersSlice);

    const { uploadImage, changeCount } = useSelector((state) => state.sharedSlice);

    useEffect(() => {
        dispatch(GetProviderEmployeeDetail(id));
        setSelectEmployeeForDelete([sessionStorage.getItem('elementForDelete')]);
    }, [
        setSupplierDocValue,
        createSupplierDocValue,
        saveProviderImage,
        imageChangeCheck,
        deletedFlag,
        uploadImage,
        changeCount,
    ]);

    /**
     * Deletes selected documents from the provider employee record.
     * Dispatches an API call and refreshes the view on success.
     *
     * @param {Array} deleteItem - List of document IDs to delete.
     */
    const deleteSelectedDocuments = (deleteItem) => {
        const tableName = USER_TYPE_DOCUMENT.CONTRACTOR_DOCUMENT;
        const body = deleteItem;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                     payload: {
                         data: { success },
                     },
                 }) => {
                    if (success === true) {
                        // Refresh view and reset selection
                        setDeletedFlag(deletedFlag + 1);
                        setSelectDocumentsForDelete([]);
                        setIsAllChecked(false);
                    }
                },
            );
        }
    };

    /**
     * Deletes a selected provider employee from the system.
     * Navigates back to the employee list on success.
     *
     * @param {Array} deleteItem - List of employee IDs to delete.
     */
    const deleteSelectedElement = (deleteItem) => {
        const tableName = TABLES.SUPPLIER_EMPLOYEE;
        const body = deleteItem;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                     payload: {
                         data: { success },
                     },
                 }) => {
                    if (success === true) {
                        // Navigate back to employee search page on successful deletion
                        navigate(routes.suppliers.employees);
                    }
                },
            );
        }
    };


    return {
        t,
        deleteDocShow,
        setDeleteDocShow,
        selectDocumentsForDelete,
        selectEmployeeForDelete,
        setSelectDocumentsForDelete,
        isAllChecked,
        setIsAllChecked,
        elementDetails: getProviderEmployeeDetail,
        deletedFlag,
        setDeletedFlag,
        deleteSelectedDocuments,
        deleteSelectedElement
    }
}
